import { useIsFocused } from "@-/com.npmjs.expo-router/react-navigation";
import { Portal, PortalHost } from "@-/com.npmjs.react-native-teleport";
import {
  PrimaryControlsStripBackContext,
  type PrimaryControlsStripBackRegistryOverrideEntry,
} from "@-/dev.improve.haniverse.contexts.primary-controls-strip.back";
import { useNavigationDepth } from "@-/dev.improve.haniverse.routes.nav._lib.back";
import { ActivityIndicator, Platform, Pressable, View } from "@-/com.npmjs.react-native";
import { useContext, useEffect, useLayoutEffect, useState, type ReactNode } from "react";
import { usePrimaryControlsStripBackRegistryCurrent } from "#use-primary-controls-strip-back-registry-current";
import { usePrimaryControlsStripLeadingIsOverridden } from "#use-primary-controls-strip-leading-is-overridden";
import {
  ToolbarGlassSurface,
  useToolbarGridMetrics,
} from "@-/dev.improve.haniverse.components.ui.toolbar.button.view";
import { Icon } from "@-/dev.improve.haniverse.components.ui.icon";
import { BoundedSuspense } from "@-/dev.improve.haniverse.components.ui.suspense-page";
import { useScopedBack } from "@-/dev.improve.haniverse.routes.nav._lib.back";
import { PrimaryControlsStripBackControl } from "#primary-controls-strip-back";
import { usePrimaryControlsStripBackGate } from "#use-primary-controls-strip-back-gate";

const isWeb = Platform.OS === "web";

const PRIMARY_CONTROLS_STRIP_LEADING_PORTAL_HOST_NAME = "primary-controls-strip-leading";

/** A missing publisher must not strand the shell on an endless loading indicator. */
const LEADING_CONTROL_LOADING_DEADLINE_MS = 30_000;

function LeadingControlLoadingFallback() {
  const { diameter } = useToolbarGridMetrics();
  const { goBack } = useScopedBack();
  const [hasExpired, setHasExpired] = useState(false);

  useEffect(() => {
    const timeout = setTimeout(setHasExpired, LEADING_CONTROL_LOADING_DEADLINE_MS, true);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <ToolbarGlassSurface style={{ height: diameter, width: diameter }}>
      <Pressable
        accessibilityLabel={hasExpired ? "Back" : "Loading page controls"}
        accessibilityRole="button"
        accessibilityState={{ busy: !hasExpired, disabled: !hasExpired }}
        className="size-full items-center justify-center"
        disabled={!hasExpired}
        hitSlop={8}
        onPress={() => goBack({ fallbackHref: "/(nav)/(app)" })}
        testID={
          hasExpired
            ? "primary-controls-strip-back-control"
            : "primary-controls-strip-leading-fallback"
        }
      >
        {hasExpired ? (
          <Icon className="text-foreground" name="FiChevronLeftBold" size={24} />
        ) : (
          <ActivityIndicator size="small" testID="primary-controls-strip-leading-spinner" />
        )}
      </Pressable>
    </ToolbarGlassSurface>
  );
}

/**
 * The app shell owns this permanent top-left slot, including its loading state. Page loading,
 * focus effects and missing publishers can never collapse it into an empty corner.
 */
export function PrimaryControlsStripLeadingHost() {
  const { diameter } = useToolbarGridMetrics();
  const isOverridden = usePrimaryControlsStripLeadingIsOverridden();
  const backGate = usePrimaryControlsStripBackGate({ appShellPlacement: "account-drawer" });
  // Teleported children are laid out against the host's own size, and they never feed back into
  // it, so the host needs an explicit box: the More trigger's circle while a page fills the slot,
  // and the same circle for the back/loading control otherwise. The More trigger and profile
  // button carry a 2px leading margin for the page actions lane's `edgeInset - 2` offset; the
  // host starts 2px early and is 2px wider so they land at the back control's 9px edge.
  return (
    <View
      className={isOverridden ? "-ml-0.5" : undefined}
      pointerEvents="box-none"
      style={{ height: diameter, width: diameter + (isOverridden ? 2 : 0) }}
      testID="primary-controls-strip-leading-slot"
    >
      <PortalHost className="size-full" name={PRIMARY_CONTROLS_STRIP_LEADING_PORTAL_HOST_NAME}>
        {isOverridden ? null : backGate !== null ? (
          <PrimaryControlsStripBackControl gate={backGate} layout="app-shell" />
        ) : (
          <LeadingControlLoadingFallback />
        )}
      </PortalHost>
    </View>
  );
}

/**
 * Overrides the app shell's top-left slot, which otherwise holds the back control. Only a
 * layout's main pages (the ones reached from a tab or the drawer) render one — for their More
 * menu, or Settings' profile button — so every other page gets the back control by default.
 *
 * It publishes into the same depth-ordered registry as `PrimaryControlsStripBack`: while its screen is
 * focused it claims the slot, and a deeper screen's back control (a nested page pushed inside the
 * same layout) takes the slot back. The children are teleported, not re-rendered, so they keep
 * the publishing screen's React context. Web has no app-shell slot, so they render in place.
 */
export function PrimaryControlsStripLeading({ children }: { children: ReactNode }) {
  return isWeb ? (
    children
  ) : (
    <PrimaryControlsStripLeadingNative>{children}</PrimaryControlsStripLeadingNative>
  );
}

function PrimaryControlsStripLeadingNative({ children }: { children: ReactNode }) {
  const isFocused = useIsFocused();
  const registry = useContext(PrimaryControlsStripBackContext);
  const depth = useNavigationDepth();
  // One distinct object per publisher: the registry's winner is compared by identity below.
  const [entry] = useState<PrimaryControlsStripBackRegistryOverrideEntry>(() => ({
    kind: "override",
  }));
  const current = usePrimaryControlsStripBackRegistryCurrent();

  // Resolve the winning publisher before paint, together with its portal, rather than clearing
  // the shell's loading fallback in a passive effect a frame ahead of the page control.
  useLayoutEffect(() => {
    if (!isFocused) {
      return;
    }
    return registry.publish(entry, { depth }).unpublish;
  }, [depth, entry, isFocused, registry]);

  // Mount the portal in the same commit that publishes its claim. Its content and the host's
  // fallback then switch from the same registry snapshot; claiming an unmounted portal would
  // leave a blank corner until the next commit.
  return isFocused ? (
    <Portal hostName={PRIMARY_CONTROLS_STRIP_LEADING_PORTAL_HOST_NAME}>
      {current === entry ? (
        // A menu that stays suspended past the app-wide Suspense deadline fails to the publishing
        // page's error screen (above this boundary in the React tree), which owns retry.
        <BoundedSuspense
          fallback={
            <View className="ml-0.5">
              <LeadingControlLoadingFallback />
            </View>
          }
          operation="primary controls strip leading controls"
        >
          {children}
        </BoundedSuspense>
      ) : null}
    </Portal>
  ) : null;
}
